import React, { useCallback, useEffect, useMemo } from "react";

import * as yup from "yup";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { FormProvider, useForm } from "react-hook-form";

import Input from "../../components/Input";

import { MUTATION_GENERATE_OPEN_DOOR_URL } from "../../config/graphql/mutation";

type FieldValues = {
  email: string;
};

const AccessPortal = React.memo(() => {
  const { t, i18n } = useTranslation(["accessPortal"]);

  const location = useLocation();

  const shellyId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("id");
  }, [location.search]);

  const language = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("lng");
  }, [location.search]);

  useEffect(() => {
    if (language && i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [language, i18n]);

  const schema = useMemo(() => {
    return yup.object().shape({
      email: yup
        .string()
        .required(t("accessPortal:yup.email.required"))
        .email(t("accessPortal:yup.email.pattern")),
    });
  }, [t]);

  const methods = useForm<FieldValues>({
    resolver: yupResolver(schema),
    defaultValues: { email: "" },
  });

  const [onGenerateOpenDoorUrl, { loading }] = useMutation(
    MUTATION_GENERATE_OPEN_DOOR_URL,
  );

  const onSubmit = useCallback(
    (input: FieldValues) => {
      return onGenerateOpenDoorUrl({
        variables: {
          input: {
            shellyControllerId: shellyId,
            employeeEmail: input.email,
            language,
          },
        },
      })
        .then(() => {
          toast.success<string>(t("accessPortal:toast.success"));
        })
        .catch((error: any) => {
          const code = error?.graphQLErrors?.[0]?.extensions?.code;

          const message =
            t(`accessPortal:toast.error.${code}`) ||
            t("accessPortal:toast.error.default");
          toast.error<string>(message);
        });
    },
    [onGenerateOpenDoorUrl, shellyId, language, t],
  );

  return (
    <FormProvider {...methods}>
      <div className="row vh-100 justify-content-center align-items-center">
        <div className="col-6">
          <h5 className="text-center mb-3">{t("accessPortal:title")}</h5>
          <p>{t("accessPortal:subtitle")}</p>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <div className="form-group">
              <label htmlFor="email">
                {t("accessPortal:form.email.label")}
              </label>
              <Input
                name="email"
                className="form-control"
                placeholder={t("accessPortal:form.email.placeholder")}
                autoComplete="email"
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {t("accessPortal:button.submit")}
            </button>
          </form>
        </div>
      </div>
    </FormProvider>
  );
});

export default AccessPortal;
